import { useCallback, useState } from 'react';
import { AiReportHelperService } from '../services/appAiReportHelperService';
import { AiReportColumnMapping, AiReportCustomFill, AiReportHistoryItem, AiReportTemplateColumn } from '../models/AiReportHelper';

export const useAiReportHelper = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [history, setHistory] = useState<AiReportHistoryItem[]>([]);

    const withLoading = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
        setLoading(true);
        setError(null);
        try {
            return await fn();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Something went wrong');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const uploadSource = useCallback((file: File) => withLoading(() => AiReportHelperService.uploadSource(file)), [withLoading]);

    const uploadTemplate = useCallback(
        (conversionId: number, file: File) => withLoading(() => AiReportHelperService.uploadTemplate(conversionId, file)),
        [withLoading]
    );

    const buildTemplate = useCallback(
        (conversionId: number, columns: string[]) => withLoading(() => AiReportHelperService.buildTemplate(conversionId, columns)),
        [withLoading]
    );

    const suggestMapping = useCallback(
        (conversionId: number) => withLoading(() => AiReportHelperService.suggestMapping(conversionId)),
        [withLoading]
    );

    const resolveFillInstruction = useCallback(
        (conversionId: number, targetColumn: string, instruction: string) =>
            withLoading(() => AiReportHelperService.resolveFillInstruction(conversionId, targetColumn, instruction)),
        [withLoading]
    );

    const suggestFillOptions = useCallback(
        (conversionId: number, targetColumn: string, instruction?: string) =>
            withLoading(() => AiReportHelperService.suggestFillOptions(conversionId, targetColumn, instruction)),
        [withLoading]
    );

    const generate = useCallback(
        (conversionId: number, selectedColumns: string[], mapping: any, columnRenames?: Record<string, string>) =>
            withLoading(() => AiReportHelperService.generate(conversionId, selectedColumns, mapping, columnRenames)),
        [withLoading]
    );

    const previewDatasetApply = useCallback(
        (conversionId: number, datasetId: number, matchSourceColumn: string, datasetColumnKeys: string[]) =>
            withLoading(() => AiReportHelperService.previewDatasetApply(conversionId, datasetId, matchSourceColumn, datasetColumnKeys)),
        [withLoading]
    );

    const addTemplateColumns = useCallback(
        (conversionId: number, labels: string[]) =>
            withLoading(() => AiReportHelperService.addTemplateColumns(conversionId, labels)),
        [withLoading]
    );

    const resolveEditInstruction = useCallback(
        (conversionId: number, targetColumnLabel: string, instruction: string) =>
            withLoading(() => AiReportHelperService.resolveEditInstruction(conversionId, targetColumnLabel, instruction)),
        [withLoading]
    );

    const applyEdit = useCallback(
        (conversionId: number, targetColumnLabel: string, customFill: AiReportCustomFill) =>
            withLoading(() => AiReportHelperService.applyEdit(conversionId, targetColumnLabel, customFill)),
        [withLoading]
    );

    const downloadGeneratedReport = useCallback(
        (conversionId: number) => withLoading(() => AiReportHelperService.downloadGeneratedReport(conversionId)),
        [withLoading]
    );

    const chat = useCallback(
        (conversionId: number, message: string) => withLoading(() => AiReportHelperService.chat(conversionId, message)),
        [withLoading]
    );

    const fetchHistory = useCallback(
        () =>
            withLoading(async () => {
                const res = await AiReportHelperService.getHistory();
                setHistory(res.history || []);
                return res;
            }),
        [withLoading]
    );

    const fetchById = useCallback((conversionId: number) => withLoading(() => AiReportHelperService.getById(conversionId)), [withLoading]);

    const deleteById = useCallback(
        (conversionId: number) =>
            withLoading(async () => {
                await AiReportHelperService.deleteById(conversionId);
                setHistory(prev => prev.filter(h => h.id !== conversionId));
            }),
        [withLoading]
    );

    const previewFile = useCallback(
        (conversionId: number, which: 'source' | 'template' | 'generated') =>
            withLoading(() => AiReportHelperService.previewFile(conversionId, which)),
        [withLoading]
    );

    const getAnalyticsData = useCallback(
        (conversionId: number, params: { xColumn: string; yColumn?: string; aggregation?: 'sum' | 'count'; groupBy?: 'day' | 'week' | 'month' | 'year' }) =>
            withLoading(() => AiReportHelperService.getAnalyticsData(conversionId, params)),
        [withLoading]
    );

    const updateMeta = useCallback(
        (conversionId: number, patch: { title?: string; generated_file_name?: string }) =>
            withLoading(async () => {
                const res = await AiReportHelperService.updateMeta(conversionId, patch);
                if (patch.title !== undefined) {
                    setHistory(prev => prev.map(h => (h.id === conversionId ? { ...h, title: patch.title! } : h)));
                }
                return res;
            }),
        [withLoading]
    );

    const classifyIntent = useCallback((message: string) => AiReportHelperService.classifyIntent(message), []);

    const listSavedTemplates = useCallback(() => withLoading(() => AiReportHelperService.listSavedTemplates()), [withLoading]);

    const saveAsTemplate = useCallback(
        (conversionId: number, name: string, templateHeaders: AiReportTemplateColumn[], mapping: AiReportColumnMapping, selectedColumns: string[]) =>
            withLoading(() => AiReportHelperService.saveAsTemplate(conversionId, name, templateHeaders, mapping, selectedColumns)),
        [withLoading]
    );

    const uploadSavedTemplateFile = useCallback(
        (file: File) => withLoading(() => AiReportHelperService.uploadSavedTemplateFile(file)),
        [withLoading]
    );

    const addSavedTemplate = useCallback(
        (name: string, templateFileName: string, templateFileUrl: string, templateHeaders: AiReportTemplateColumn[], selectedColumns: string[]) =>
            withLoading(() => AiReportHelperService.addSavedTemplate(name, templateFileName, templateFileUrl, templateHeaders, selectedColumns)),
        [withLoading]
    );

    const renameSavedTemplate = useCallback(
        (templateId: number, name: string) => withLoading(() => AiReportHelperService.renameSavedTemplate(templateId, name)),
        [withLoading]
    );

    const loadSavedTemplate = useCallback(
        (conversionId: number, savedTemplateId: number) =>
            withLoading(() => AiReportHelperService.loadSavedTemplate(conversionId, savedTemplateId)),
        [withLoading]
    );

    const deleteSavedTemplate = useCallback(
        (templateId: number) => withLoading(() => AiReportHelperService.deleteSavedTemplate(templateId)),
        [withLoading]
    );

    return {
        loading,
        error,
        history,
        uploadSource,
        uploadTemplate,
        buildTemplate,
        suggestMapping,
        resolveFillInstruction,
        suggestFillOptions,
        generate,
        previewDatasetApply,
        addTemplateColumns,
        resolveEditInstruction,
        applyEdit,
        downloadGeneratedReport,
        chat,
        fetchHistory,
        fetchById,
        deleteById,
        updateMeta,
        previewFile,
        getAnalyticsData,
        classifyIntent,
        listSavedTemplates,
        saveAsTemplate,
        uploadSavedTemplateFile,
        addSavedTemplate,
        renameSavedTemplate,
        loadSavedTemplate,
        deleteSavedTemplate,
    };
};
